/**
 * Qué tan buena es una contraseña, mientras se escribe.
 *
 * **Esto es una cortesía, no la autoridad.** Quien decide es
 * `backend/src/services/contrasena.service.js`, y sus reglas se aplican en el
 * servidor porque son las únicas que un atacante no puede saltarse. Este
 * archivo existe para que no tengas que enviar el formulario para descubrir
 * qué falta: es un espejo, y si alguna vez discrepan, manda el servidor.
 *
 * ## Por qué la lista no dice «una mayúscula, un número y un símbolo»
 *
 * Porque esas reglas empeoran las contraseñas. Obligado a cumplirlas, casi
 * todo el mundo escribe «Password1!» —mayúscula delante, número y símbolo
 * detrás— y esas transformaciones son las primeras que prueba un atacante.
 *
 * Lo que de verdad cuesta romper es la longitud. Por eso la lista pide cosas
 * distintas: que sea larga, que no sea de las conocidas, y que no salga de tus
 * propios datos. Son las tres que mueven la aguja.
 */

export const LONGITUD_MINIMA = 10;
export const LONGITUD_COMODA = 16;

const CONOCIDAS = new Set([
    'password', 'passw0rd', 'pass', 'contrasena', 'clave', 'secreto', 'secret',
    'qwerty', 'qwertyui', 'qwertyuiop', 'asdfgh', 'asdfghjk', 'zxcvbn', 'qazwsx', '1q2w3e4r',
    'abc', 'abcd', 'abcdef', 'abcdefg', 'abcdefgh', 'letmein', 'welcome', 'bienvenido',
    'admin', 'administrador', 'root', 'usuario', 'user', 'test', 'prueba', 'demo',
    'iloveyou', 'teamo', 'princess', 'princesa', 'sunshine', 'monkey', 'dragon',
    'football', 'futbol', 'baseball', 'basketball', 'superman', 'batman', 'pokemon',
    'master', 'maestro', 'shadow', 'michael', 'jennifer', 'jordan', 'hunter',
    'trustno', 'whatever', 'freedom', 'starwars', 'computer', 'internet', 'ordenador',
    'hola', 'holahola', 'holamundo', 'buenos', 'mexico', 'espana', 'madrid',
    'barcelona', 'colombia', 'venezuela', 'argentina', 'dominicana', 'santodomingo',
    'shippingfull', 'dropshipping', 'ebay', 'amazon', 'google', 'facebook',
    'temporal', 'cambiar', 'cambiame', 'changeme', 'nueva', 'nuevo'
]);

/** Las dos formas en que una contraseña conocida intenta disfrazarse. */
function raices(texto) {
    const base = String(texto).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const soloLetras = base.replace(/[^a-z]/g, '');
    const traducida = base
        .replace(/[\W_]+$/, '')
        .replace(/\d+$/, '')
        .replace(/[@4]/g, 'a').replace(/0/g, 'o').replace(/[1!|]/g, 'i')
        .replace(/3/g, 'e').replace(/[5$]/g, 's').replace(/7/g, 't')
        .replace(/[^a-z]/g, '');

    return [soloLetras, traducida].filter(Boolean);
}

function esSecuencia(texto) {
    const filas = ['abcdefghijklmnopqrstuvwxyz', '0123456789', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
    const t = String(texto).toLowerCase();
    return filas.some((f) => f.includes(t) || [...f].reverse().join('').includes(t));
}

/**
 * @returns {{nivel: number, etiqueta: string, reglas: object, valida: boolean}}
 *
 * `reglas` es lo que se pinta como lista con su marca: ver qué falta mientras
 * escribes es lo que convierte «contraseña rechazada» en algo accionable.
 */
export function medirContrasena(contrasena, { email = '', nombre = '' } = {}) {
    const valor = String(contrasena || '');
    const nucleos = raices(valor);

    const propios = [String(email).split('@')[0], ...String(nombre).split(/\s+/)]
        .flatMap(raices)
        .filter((p) => p.length >= 3);

    const reglas = {
        longitud: valor.length >= LONGITUD_MINIMA,
        noConocida: valor.length > 0 && !nucleos.some((n) => CONOCIDAS.has(n)),
        noPersonal: valor.length > 0 && !nucleos.some((n) => propios.some((p) => n.includes(p) || p.includes(n))),
        noTrivial: valor.length > 0 && !/^(.)\1+$/.test(valor) && !esSecuencia(valor) && !/^\d+$/.test(valor)
    };

    const valida = Object.values(reglas).every(Boolean);

    // El nivel premia lo que de verdad importa: pasar las reglas, y luego la
    // longitud. Una contraseña válida de 10 es «suficiente»; una de 16 o más
    // es donde empieza a ser difícil de verdad.
    let nivel = 0;
    if (valor.length > 0) nivel = 1;
    if (valida) nivel = 2;
    if (valida && valor.length >= 13) nivel = 3;
    if (valida && valor.length >= LONGITUD_COMODA) nivel = 4;

    const etiquetas = ['', 'password_strength.weak', 'password_strength.ok', 'password_strength.good', 'password_strength.strong'];

    return { nivel, etiqueta: etiquetas[nivel], reglas, valida };
}

export default medirContrasena;
